<template>
  <div class="d-flex justify-content-between align-items-end mb-2">
    <div class="form-label m-0">{{ $t('campaigns.google_ads.marketing_images') }}</div>
    <div class="text-muted" style="font-size: 13px">
      <span :class="{ 'text-danger': selectedImages.length >= maxImages }">{{ selectedImages.length }}</span> /
      {{ maxImages }} {{ $t('campaigns.google_ads.images_used') }}
    </div>
  </div>

  <div class="images-grid mb-3" ref="imagesListEl">
    <AdImagePicker
      v-for="(image, index) in selectedImages"
      :key="image.id"
      class="image-preview-wrapper"
      :data-id="image.id"
      :imageUrl="image.proxy?.url_raw || image.proxy?.url_thumbnail || `${hostname}/v1/media/images/${image.id}/jump`"
      boxSize
      sortable
      @remove="removeImage(index)" />
    <AdImagePicker
      v-if="selectedImages.length < maxImages"
      :uploadText="$t('campaigns.choose_image')"
      boxSize
      class="ignore-sort"
      @click="openPicker()" />
  </div>

  <div v-if="props.errors">
    <FormErrorList :errors="props.errors" />
  </div>

  <ImagePickerModal
    v-model="designerModal"
    :images="banners.map((b) => ({ id: b.id, thumbnailUrl: b.proxy?.url_thumbnail }))"
    :selectedIds="selectedImages.map((img) => img.id)"
    :maxImages="maxImages"
    :showCount="true"
    :title="$t('campaigns.choose_image')"
    :uploadText="$t('campaigns.choose_image')"
    @select="toggleImageSelectionById" />
</template>

<script setup lang="ts">
import { FormErrorList } from '@prospective/lithium';
import { ref, watch, onMounted, nextTick, onUnmounted } from 'vue';
import { hostname, request } from '@/api/client';
import { fetchCompanyImages } from '@/api/company';
import type { ImageObject } from '@/types/types';
import Sortable from 'sortablejs';
import AdImagePicker from './AdImagePicker.vue';
import ImagePickerModal from './ImagePickerModal.vue';

const props = withDefaults(
  defineProps<{
    modelValue: { id: number; type: string }[];
    errors?: string[];
    companyId?: number;
    maxImages?: number;
  }>(),
  {
    modelValue: () => [],
    errors: () => [],
    maxImages: 7,
  }
);

const designerModal = ref(false);
const banners = ref<ImageObject[]>([]);
const selectedImages = ref<ImageObject[]>([]);
const imagesListEl = ref<HTMLElement | null>(null);
let sortable: Sortable | null = null;

const emit = defineEmits<{
  (e: 'update:modelValue', value: { id: number; type: string }[]): void;
}>();

const initSortable = () => {
  if (imagesListEl.value && !sortable) {
    sortable = Sortable.create(imagesListEl.value, {
      animation: 150,
      draggable: '.image-preview-wrapper',
      filter: '.ignore-sort',
      handle: '.drag-handle',
      ghostClass: 'sortable-ghost',
      dragClass: 'sortable-drag',
      onEnd: (evt) => {
        if (evt.oldIndex !== undefined && evt.newIndex !== undefined) {
          const newImages = [...selectedImages.value];
          const movedItem = newImages.splice(evt.oldIndex, 1)[0];
          newImages.splice(evt.newIndex, 0, movedItem);
          selectedImages.value = newImages;
          emitModelValue();
        }
      },
    });
  }
};

onMounted(() => {
  loadSelectedImagesDetails();
});

onUnmounted(() => {
  if (sortable) {
    sortable.destroy();
    sortable = null;
  }
});

watch(
  () => props.modelValue,
  (newVal) => {
    // Only reload if the IDs differ from what we have locally
    const currentIds = selectedImages.value.map((img) => img.id);
    const newIds = (newVal || []).map((item) => item.id);

    if (JSON.stringify(currentIds) !== JSON.stringify(newIds)) {
      loadSelectedImagesDetails();
    }
  },
  { deep: true }
);

watch(imagesListEl, () => {
  if (imagesListEl.value) {
    initSortable();
  }
});

const loadSelectedImagesDetails = async () => {
  if (!props.modelValue || props.modelValue.length === 0) {
    selectedImages.value = [];
    return;
  }

  const promises = props.modelValue.map((item) => request('GET', `/v1/account/media/images/${item.id}`, { meta: 1 }));
  const results = await Promise.allSettled(promises);

  const images: ImageObject[] = [];
  results.forEach((res) => {
    if (res.status === 'fulfilled' && res.value.data) {
      images.push(res.value.data);
    }
  });

  selectedImages.value = props.modelValue
    .map((item) => images.find((img) => img.id === item.id))
    .filter((img): img is ImageObject => !!img);

  nextTick(() => initSortable());
};

const openPicker = () => {
  designerModal.value = true;
  fetchCompanyImages(props.companyId || 0, { type: 'image' }).then((res) => {
    banners.value = res?.data || [];
  });
};

const toggleImageSelectionById = (id: number) => {
  const banner = banners.value.find((b) => b.id === id);
  if (banner) toggleImageSelection(banner);
};

const toggleImageSelection = (banner: ImageObject) => {
  const index = selectedImages.value.findIndex((img) => img.id === banner.id);
  if (index !== -1) {
    selectedImages.value = selectedImages.value.filter((img) => img.id !== banner.id);
  } else {
    if (selectedImages.value.length < props.maxImages) {
      selectedImages.value = [...selectedImages.value, banner];
    }
  }
  emitModelValue();
};

const removeImage = (index: number) => {
  const newImages = [...selectedImages.value];
  newImages.splice(index, 1);
  selectedImages.value = newImages;
  emitModelValue();
};

const emitModelValue = () => {
  emit(
    'update:modelValue',
    selectedImages.value.map((img) => ({ id: img.id, type: 'image' }))
  );
};
</script>

<style scoped lang="scss">
.form-label {
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 6px;
  color: #344054;
}

.images-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: flex-start;
}

.modal-images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 10px;
  align-items: center;
}

:deep(.sortable-ghost) {
  opacity: 0.4;
  border: 2px dashed var(--tw-color-primary);
  background-color: #f8f9fa;
  box-sizing: border-box;
}

:deep(.sortable-drag) {
  cursor: grabbing !important;
  opacity: 1 !important;
}
</style>
